// src/modules/app-views/omnichannel/pages/Init/components/MessageBubble.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Divider, Flex, Tooltip, theme } from 'antd';
import omnichannelService from '../../../service';
import { updateMessageDurationInCache } from './ChatMenu';
import {
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  TranslationOutlined,
  LoadingOutlined,
  PlayCircleFilled,
  DownloadOutlined,
} from '@ant-design/icons';
import { TRANSLATE_LANGUAGES, renderFlag } from './TranslateLanguageSelect';
import { getCountryMetadata } from 'utils/phone';
import translateX from 'utils/translateX';
import ReactionPicker from './ReactionPicker';
import MediaPreviewModal from './MediaPreviewModal';
import { PiArrowBendDoubleUpRightBold } from 'react-icons/pi';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// WhatsApp brand colors - not AntD tokens, intentional palette match
const WA_DARK_OUT_BG = '#154D37'; // verify-ignore
const WA_DARK_IN_BG = '#1f2c34'; // verify-ignore
const WA_LIGHT_OUT_BG = '#dcf8c6'; // verify-ignore
const WA_TEXT_DARK = '#111b21'; // verify-ignore
const WA_TEXT_LIGHT = '#e9edef'; // verify-ignore
const WA_GREEN = '#00a884'; // verify-ignore
const WA_GREEN_LIGHT = '#06cf9c'; // verify-ignore
const WA_BLUE_READ = '#53bdeb'; // verify-ignore

// WhatsApp group sender color palette
const SENDER_COLORS = [
  '#06cf9c', '#25d366', '#00a884', '#53bdeb', '#007bfc', // verify-ignore
  '#ff6b6b', '#ffa726', '#ab47bc', '#ec407a', '#5c6bc0', // verify-ignore
  '#26a69a', '#8d6e63', '#78909c', '#d4e157', // verify-ignore
];

// Static style constants
const FORWARDED_STYLE = {
  fontSize: 11,
  fontStyle: 'italic',
  opacity: 0.6,
  marginBottom: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 3,
};

const QUOTED_CONTENT_STYLE = {
  opacity: 0.75,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 260,
};

const TIMESTAMP_ROW = {
  position: 'absolute',
  bottom: 5,
  right: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 10,
  whiteSpace: 'nowrap',
};

const FLAG_TOGGLE_BASE = {
  lineHeight: 1,
  cursor: 'pointer',
  transition: 'opacity 0.15s',
};

const VIDEO_OVERLAY = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.25)',
  borderRadius: 6,
};

const DOC_META_ROW = { fontSize: 11, opacity: 0.55, marginTop: 1 };

const POLL_OPTION_STYLE = {
  padding: '6px 10px',
  marginBottom: 4,
  borderRadius: 6,
  border: '1px solid rgba(0,0,0,0.12)',
  fontSize: 13,
};

const LOADING_OVERLAY = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  borderRadius: 24,
};

const DOC_ICON_STYLE = { fontSize: 28 };

// *******************************************************************************
// COMPONENT
// *******************************************************************************

const MessageBubble = ({
  msg,
  msgIndex,
  conversationId,
  isGroup,
  isFirstInGroup,
  translation,
  translatingIndex,
  autoTranslatingIndices,
  outboundOriginal,
  outboundDeliveryLang,
  outboundTranslateError,
  onTranslateMessage,
  onReaction,
  isDark,
  tailShadow,
  tailClass,
  token,
}) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { token: themeToken } = theme.useToken();
  const activeToken = token || themeToken;

  // 4. LOCAL STATE
  const [hovered, setHovered] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);

  // 5. LOCAL VARIABLES II
  const isInbound = msg.direction === 'inbound';
  const isOutbound = msg.direction === 'outbound';

  const bubbleStyle = isDark
    ? isOutbound
      ? { backgroundColor: WA_DARK_OUT_BG, color: WA_TEXT_LIGHT }
      : { backgroundColor: WA_DARK_IN_BG, color: WA_TEXT_LIGHT }
    : isOutbound
      ? { backgroundColor: WA_LIGHT_OUT_BG, color: WA_TEXT_DARK }
      : { backgroundColor: '#ffffff', color: WA_TEXT_DARK }; // verify-ignore

  const bubbleWrapperStyle = { position: 'relative', paddingBottom: 26, minWidth: 100, ...bubbleStyle };

  const reactionAbsStyle = { position: 'absolute', top: 0, [isOutbound ? 'left' : 'right']: 0, zIndex: 2 };

  const reactionContainerStyle = {
    display: 'flex',
    justifyContent: isOutbound ? 'flex-end' : 'flex-start',
    marginTop: -8,
    marginBottom: 4,
    paddingLeft: isInbound ? 8 : 0,
    paddingRight: isOutbound ? 8 : 0,
  };

  const reactionBadgeStyle = {
    fontSize: 16,
    background: isDark ? WA_DARK_IN_BG : '#ffffff', // verify-ignore
    borderRadius: 12,
    padding: '0 6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    cursor: 'default',
    zIndex: 1,
    height: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  };

  const quotedStyle = {
    borderLeft: `3px solid ${isDark ? WA_GREEN : WA_GREEN_LIGHT}`,
    borderRadius: 4,
    padding: '4px 8px',
    marginBottom: 4,
    fontSize: 12,
    background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
    cursor: 'default',
    overflow: 'hidden',
  };

  const quotedSenderStyle = {
    fontWeight: 600,
    color: isDark ? WA_GREEN : WA_GREEN_LIGHT,
    fontSize: 11,
    marginBottom: 1,
  };

  const translationIconStyle = {
    cursor: 'pointer',
    opacity: translation ? 1 : 0.5,
    color: translation ? activeToken.colorPrimary : 'inherit',
    fontSize: 11,
  };

  // 6. HANDLERS
  const handleMediaPreview = useCallback((mediaUrl, mediaType, caption) => {
    setPreviewMedia({ mediaUrl, mediaType, caption });
  }, []);

  // 9. UI
  if (!msg.content?.text && !msg.content?.mediaUrl && !msg.content?.mediaType) {
    if (msg.timestamp) {
      return <Divider>{new Date(msg.timestamp).toLocaleTimeString()}</Divider>;
    }
    return null;
  }

  const currentReactionEmoji = findCurrentReactionEmoji(msg.reactions);

  return (
    <div
      className={`bubble ${tailClass} ${isInbound && !msg.sender?.avatar && !isGroup ? '' : ''}`}
      style={{ filter: tailShadow, position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && onReaction && (
        <div style={reactionAbsStyle}>
          <ReactionPicker
            msgIndex={msgIndex}
            currentReactionEmoji={currentReactionEmoji}
            onReact={onReaction}
            isDark={isDark}
            direction={msg.direction}
          />
        </div>
      )}

      <div className="bubble-wrapper" style={bubbleWrapperStyle}>
        {isGroup && isFirstInGroup && isInbound && msg.sender?.name && (
          <div style={{ fontSize: 12, fontWeight: 600, color: getSenderColorFromName(msg.sender.name), marginBottom: 2, lineHeight: 1.3 }}>
            {msg.sender.name}
          </div>
        )}

        {msg.forwarded && (
          <div style={FORWARDED_STYLE}>
            <PiArrowBendDoubleUpRightBold style={{ fontSize: 12 }} />
            {msg.frequentlyForwarded ? 'Encaminhada muitas vezes' : 'Encaminhada'}
          </div>
        )}

        {msg.quotedMessage && (
          <div style={quotedStyle}>
            <div style={quotedSenderStyle}>
              {msg.quotedMessage.senderType === 'agent' ? translateX('you') : (msg.quotedMessage.senderName || translateX('contact'))}
            </div>
            <div style={QUOTED_CONTENT_STYLE}>
              {msg.quotedMessage.text
                || msg.quotedMessage.caption
                || (msg.quotedMessage.mediaType === 'image' ? translateX('image') : null)
                || (msg.quotedMessage.mediaType === 'document' ? (msg.quotedMessage.fileName || translateX('document')) : null)
                || (msg.quotedMessage.mediaType === 'audio' ? translateX('audio') : null)
                || (msg.quotedMessage.mediaType === 'video' ? translateX('video') : null)
                || '...'}
            </div>
          </div>
        )}

        {isOutbound && outboundOriginal ? (
          translation ? (
            renderWhatsAppText(translation.text)
          ) : (
            renderWhatsAppText(outboundOriginal)
          )
        ) : autoTranslatingIndices.has(msgIndex) ? (
          <span style={{ opacity: 0.65, fontStyle: 'italic' }}>traduzindo...</span>
        ) : translation ? (
          renderWhatsAppText(translation.text)
        ) : (
          renderMsgType(msg, handleMediaPreview, conversationId, activeToken)
        )}

        <div style={TIMESTAMP_ROW}>
          {isOutbound &&
            outboundDeliveryLang &&
            (() => {
              const langEntry = TRANSLATE_LANGUAGES.find(l => l.lang === outboundDeliveryLang);
              if (!langEntry) return null;
              const meta = getCountryMetadata(langEntry.country);
              const countryName = meta?.names?.pt || meta?.names?.en || langEntry.country;

              // Translation failed - show flag + exclamation with error tooltip
              if (outboundTranslateError) {
                const errorTooltip = getTranslateErrorTooltip(outboundTranslateError, countryName);
                return (
                  <Flex align="center" gap={2} style={{ lineHeight: 1 }}>
                    <span style={{ opacity: 0.65 }}>
                      {renderFlag(meta, langEntry.country)}
                    </span>
                    <Tooltip title={errorTooltip} overlayStyle={{ maxWidth: 360 }} styles={{ container: { whiteSpace: 'pre-line', fontSize: 11 } }}>
                      <ExclamationCircleOutlined style={{ fontSize: 11, color: activeToken.colorWarning, cursor: 'default' }} />
                    </Tooltip>
                  </Flex>
                );
              }

              // Normal case - flag toggles between original and delivered text
              const tooltipTitle = translation
                ? translateX('show_original')
                : `${translateX('show_translated')} (${countryName})`;
              return (
                <Tooltip title={tooltipTitle}>
                  <span style={{ ...FLAG_TOGGLE_BASE, opacity: translation ? 1 : 0.65 }} onClick={e => { e.stopPropagation(); onTranslateMessage(msgIndex); }}>
                    {renderFlag(meta, langEntry.country)}
                  </span>
                </Tooltip>
              );
            })()}

          {msg.content?.text && !isOutbound && (
            <Tooltip title={translation ? translateX('show_original') : translateX('translate_message')}>
              {translatingIndex === msgIndex ? (
                <LoadingOutlined style={{ fontSize: 11, opacity: 0.65 }} />
              ) : (
                <TranslationOutlined
                  style={translationIconStyle}
                  onClick={e => { e.stopPropagation(); onTranslateMessage(msgIndex); }}
                />
              )}
            </Tooltip>
          )}

          <span style={{ opacity: 0.65 }}>{formatMsgTime(msg.timestamp)}</span>
          {isOutbound && renderMessageStatus(msg.status || 'queued', activeToken)}
        </div>
      </div>

      {msg.reactions?.length > 0 && (
        <div style={reactionContainerStyle}>
          <Tooltip title={msg.reactions.map(r => r.senderName || '').join(', ')}>
            <span style={reactionBadgeStyle}>
              {msg.reactions.map(r => r.emoji).join('')}
            </span>
          </Tooltip>
        </div>
      )}

      {previewMedia && (
        <MediaPreviewModal
          open={!!previewMedia}
          onClose={() => setPreviewMedia(null)}
          mediaUrl={previewMedia.mediaUrl}
          mediaType={previewMedia.mediaType}
          caption={previewMedia.caption}
        />
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MessageBubble;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function formatMsgTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function renderMessageStatus(status, token) {
  const style = { fontSize: 11 };

  if (status === 'failed') {
    return <span style={{ ...style, color: token.colorError }}><ExclamationCircleOutlined /></span>;
  }
  if (status === 'queued') {
    return <span style={style}><ClockCircleOutlined /></span>;
  }
  if (status === 'sent') {
    return <span style={style}><CheckOutlined /></span>;
  }
  if (status === 'delivered') {
    return <span style={style}><CheckOutlined /><CheckOutlined style={{ marginLeft: -6 }} /></span>;
  }
  if (status === 'read') {
    return (
      <span style={style}>
        <CheckOutlined style={{ color: WA_BLUE_READ }} />
        <CheckOutlined style={{ marginLeft: -6, color: WA_BLUE_READ }} />
      </span>
    );
  }
  // Fallback - double gray check (delivered, no read receipt)
  return <span style={style}><CheckOutlined /><CheckOutlined style={{ marginLeft: -6 }} /></span>;
}

function findCurrentReactionEmoji(reactions) {
  if (!reactions?.length) return null;
  const agentReaction = reactions.find(r => r.senderType === 'agent');
  return agentReaction?.emoji || null;
}

function renderMsgType(msg, onMediaPreview, conversationId, token) {
  const text = msg.content?.text;
  const caption = msg.content?.caption;
  const mediaType = msg.content?.mediaType;
  const mediaUrl = msg.content?.mediaUrl;
  const fileName = msg.content?.fileName;

  if (mediaType && !mediaUrl && !text) {
    if (mediaType === 'audio') {
      return (
        <div style={{ position: 'relative', minWidth: 240, maxWidth: '100%' }}>
          <audio controls style={{ maxWidth: '100%', minWidth: 240, opacity: 0.4, pointerEvents: 'none' }} />
          <div style={LOADING_OVERLAY}>
            <LoadingOutlined style={{ fontSize: 16, color: token.colorTextTertiary }} spin />
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.6, fontStyle: 'italic', minWidth: 140 }}>
        <LoadingOutlined style={{ fontSize: 14 }} />
        <span style={{ fontSize: 12 }}>{translateX('loading')} {translateX(mediaType) || translateX('media')}...</span>
      </div>
    );
  }

  if (mediaType === 'image' && mediaUrl) {
    return (
      <div>
        <img
          src={mediaUrl}
          alt={caption || text || 'image'}
          onClick={() => onMediaPreview?.(mediaUrl, 'image', caption)}
          style={{ width: 240, maxWidth: '100%', borderRadius: 6, display: 'block', cursor: 'pointer' }}
        />
        {caption && <span style={{ display: 'block', marginTop: 4 }}>{caption}</span>}
      </div>
    );
  }

  if (mediaType === 'sticker' && mediaUrl) {
    return <img src={mediaUrl} alt="sticker" style={{ maxWidth: 140, borderRadius: 6, display: 'block' }} />;
  }

  if (mediaType === 'video' && mediaUrl) {
    return (
      <div>
        <div onClick={() => onMediaPreview?.(mediaUrl, 'video', caption)} style={{ position: 'relative', cursor: 'pointer', width: 240, maxWidth: '100%' }}>
          <video src={mediaUrl} preload="metadata" muted playsInline style={{ width: '100%', borderRadius: 6, display: 'block' }} />
          <div style={VIDEO_OVERLAY}>
            <PlayCircleFilled style={{ fontSize: 44, color: token.colorWhite, opacity: 0.9 }} />
          </div>
        </div>
        {caption && <span style={{ display: 'block', marginTop: 4 }}>{caption}</span>}
      </div>
    );
  }

  if (mediaType === 'audio' && mediaUrl) {
    return (
      <audio
        key={mediaUrl}
        controls
        preload="auto"
        style={{ maxWidth: '100%', minWidth: 240 }}
        onLoadedMetadata={e => {
          const dur = Math.round(e.target.duration);
          if (dur > 0 && !msg.content?.duration && msg.externalMessageId && conversationId) {
            omnichannelService.updateMessageDuration(conversationId, msg.externalMessageId, dur);
            updateMessageDurationInCache(conversationId, msg.externalMessageId, dur);
          }
        }}
      >
        <source src={mediaUrl} type={mediaUrl.endsWith('.ogg') ? 'audio/ogg; codecs=opus' : 'audio/mpeg'} />
      </audio>
    );
  }

  if ((mediaType === 'document' || fileName) && mediaUrl) {
    const ext = getFileExtension(fileName);
    const docIcon = getDocumentIcon(fileName, token);
    const displayName = formatFileName(fileName);
    const sizeLabel = formatFileSize(msg.content?.fileSize);

    return (
      <div>
        <Flex
          align="center"
          gap={10}
          style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.06)', minWidth: 220, maxWidth: 300, cursor: 'pointer' }}
          onClick={() => onMediaPreview?.(mediaUrl, 'document', caption)}
        >
          {docIcon}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </div>
            <div style={DOC_META_ROW}>
              {[sizeLabel, ext?.toUpperCase()].filter(Boolean).join(' | ')}
            </div>
          </div>
          <Tooltip title={translateX('download')}>
            <DownloadOutlined
              style={{ fontSize: 16, opacity: 0.45 }}
              onClick={e => { e.stopPropagation(); triggerDownload(mediaUrl, fileName || `document.${ext || 'pdf'}`); }}
            />
          </Tooltip>
        </Flex>
        {caption && <span style={{ display: 'block', marginTop: 4 }}>{caption}</span>}
      </div>
    );
  }

  // Poll - read-only display of question + options
  if (mediaType === 'poll') {
    const options = msg.content?.pollOptions || [];
    const selectableCount = msg.content?.pollSelectableCount || 1;
    return (
      <div style={{ minWidth: 220, maxWidth: 300 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{text}</div>
        <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 8 }}>
          {selectableCount === 1 ? translateX('single_choice') : translateX('multiple_choice')}
        </div>
        {options.map((opt, idx) => (
          <div key={idx} style={POLL_OPTION_STYLE}>{opt}</div>
        ))}
      </div>
    );
  }

  // Unsupported message type - styled placeholder (like WhatsApp Web)
  if (text && isUnsupportedMessage(text)) {
    return (
      <Flex align="center" gap={8} style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.04)', border: '1px dashed rgba(0,0,0,0.15)', minWidth: 200, maxWidth: 300 }}>
        <ExclamationCircleOutlined style={{ fontSize: 16, color: token.colorWarning, flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: token.colorTextSecondary, lineHeight: 1.4 }}>
          {translateX('unsupported_message_placeholder')}
        </span>
      </Flex>
    );
  }

  // Plain text - render with WhatsApp formatting (bold, newlines) and clickable links
  if (text) return renderWhatsAppText(text);

  return null;
}

function renderWhatsAppText(text) {
  if (!text) return null;

  const tokenPattern = /(\*[^*\n]+\*|https?:\/\/[^\s]+|\n)/g;
  const tokens = [];
  let lastIndex = 0;
  let match;

  while ((match = tokenPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    if (match[0] === '\n') {
      tokens.push({ type: 'br' });
    } else if (match[0].startsWith('*')) {
      tokens.push({ type: 'bold', value: match[0].slice(1, -1) });
    } else {
      tokens.push({ type: 'url', value: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'text', value: text.slice(lastIndex) });
  }

  if (tokens.length === 1 && tokens[0].type === 'text') {
    return <span>{tokens[0].value}</span>;
  }

  return (
    <span>
      {tokens.map((token, i) => {
        if (token.type === 'br') return <br key={i} />;
        if (token.type === 'bold') return <strong key={i}>{token.value}</strong>;
        if (token.type === 'url') {
          return <a key={i} href={token.value} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{token.value}</a>;
        }
        return <span key={i}>{token.value}</span>;
      })}
    </span>
  );
}

// Trigger browser download - fetches as blob to bypass cross-origin restrictions
async function triggerDownload(url, filename) {
  const cacheBustUrl = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
  try {
    const res = await fetch(cacheBustUrl);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch {
    window.open(url, '_blank');
  }
}

function isUnsupportedMessage(text) {
  if (!text) return false;
  const t = text.trim().toLowerCase();
  return t.startsWith('[unsupported') || t.startsWith('[tipo de mensagem');
}

function getFileExtension(fileName) {
  if (!fileName) return null;
  return fileName.split('.').pop()?.toLowerCase() || null;
}

function formatFileSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getDocumentIcon(fileName, token) {
  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FilePdfOutlined style={{ ...DOC_ICON_STYLE, color: token.colorError }} />;
  if (['doc', 'docx'].includes(ext)) return <FileWordOutlined style={{ ...DOC_ICON_STYLE, color: token.colorInfo }} />;
  if (['xls', 'xlsx'].includes(ext)) return <FileExcelOutlined style={{ ...DOC_ICON_STYLE, color: token.colorSuccess }} />;
  return <FileOutlined style={DOC_ICON_STYLE} />;
}

function formatFileName(fileName) {
  if (!fileName) return translateX('document');
  if (fileName.length > 35) {
    const ext = fileName.split('.').pop();
    return `${fileName.substring(0, 28)}...${ext}`;
  }
  return fileName;
}

function getSenderColorFromName(name) {
  if (!name) return SENDER_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return SENDER_COLORS[Math.abs(hash) % SENDER_COLORS.length];
}

function getTranslateErrorTooltip(errorType, countryName) {
  if (errorType === 'timeout') {
    return (
      `Falha ao traduzir para ${countryName}. Mensagem entregue no idioma original.\n\n` +
      `Causa provavel: sua rede esta bloqueando o servico de traducao (Azure Cognitive Services).\n\n` +
      `Solucoes:\n` +
      `1. Troque de rede (ex: hotspot do celular)\n` +
      `2. Libere *.microsofttranslator.com na porta 443\n` +
      `3. Verifique filtros DNS (Pi-hole, Cloudflare Gateway)`
    );
  }
  return (
    `Falha ao traduzir para ${countryName}. Mensagem entregue no idioma original.\n\n` +
    `O servico de traducao retornou um erro. Tente novamente mais tarde ou troque de rede.`
  );
}
